
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { TelegramWebAppProvider } from './BenchmarkWebApp/components/TelegramWebAppProvider';
import { UserVerification, AccessDeniedCard } from './BenchmarkWebApp/components/UserVerification';
import { BenchmarkForm } from './BenchmarkWebApp/components/BenchmarkForm';
import { useFormSubmission } from './BenchmarkWebApp/hooks/useFormSubmission';

// Telegram WebApp interface
declare global {
  interface Window {
    Telegram?: {
      WebApp: {
        ready: () => void;
        expand: () => void;
        close: () => void;
        MainButton: {
          text: string;
          show: () => void;
          hide: () => void;
          enable: () => void;
          disable: () => void;
          onClick: (callback: () => void) => void;
          offClick: (callback: () => void) => void;
        };
        themeParams: {
          bg_color?: string;
          text_color?: string;
          hint_color?: string;
          button_color?: string;
          button_text_color?: string;
          secondary_bg_color?: string;
        };
        showAlert: (message: string, callback?: () => void) => void;
        enableClosingConfirmation: () => void;
        disableClosingConfirmation: () => void;
        HapticFeedback: {
          impactOccurred: (style: 'light' | 'medium' | 'heavy') => void;
        };
      };
    };
  }
}

interface FormData {
  marketType: string;
  productName: string;
  price: string;
  location: string;
  productRemark: string;
  productCategory: string;
}

export type CategoryType = 'fruits-vegetables' | 'fmcg';

interface UserProfile {
  id: string;
  first_name: string;
  last_name: string;
  role: string;
}

const BenchmarkWebApp: React.FC = () => {
  const [searchParams] = useSearchParams();
  const userId = searchParams.get('user_id');
  
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [accessError, setAccessError] = useState<string | null>(null);
  const [date, setDate] = useState<Date>(new Date());
  const [category, setCategory] = useState<CategoryType>('fruits-vegetables');
  const [formData, setFormData] = useState<FormData>({
    marketType: '',
    productName: '',
    price: '',
    location: '',
    productRemark: '',
    productCategory: ''
  });

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      // Reset location when market type changes
      if (field === 'marketType' && value !== prev.marketType) {
        newData.location = '';
      }
      return newData;
    });
  };

  const handleFormSuccess = () => {
    // Reset form on success
    setFormData({
      marketType: category === 'fmcg' ? 'merkato' : '',
      productName: '',
      price: '',
      location: '',
      productRemark: '',
      productCategory: ''
    });
    setDate(new Date());
  };

  const handleCategoryChange = (newCategory: CategoryType) => {
    setCategory(newCategory);
    setFormData({
      marketType: newCategory === 'fmcg' ? 'merkato' : '',
      productName: '',
      price: '',
      location: '',
      productRemark: '',
      productCategory: ''
    });
  };

  const { isSubmitting, submitForm } = useFormSubmission({
    userId: userId || '',
    formData,
    date,
    onSuccess: handleFormSuccess
  });

  // Show main button when form is ready
  useEffect(() => {
    const tg = window.Telegram?.WebApp;
    if (tg && userProfile) {
      tg.MainButton.show();
    }
  }, [userProfile]);

  if (!userId || accessError) {
    return <AccessDeniedCard userId={userId} accessError={accessError} />;
  }

  if (!userProfile) {
    return (
      <UserVerification
        userId={userId}
        onVerified={setUserProfile}
        onError={setAccessError}
      />
    );
  }

  return (
    <TelegramWebAppProvider onSubmit={submitForm} isSubmitting={isSubmitting}>
      <BenchmarkForm
        userProfile={userProfile}
        formData={formData}
        date={date}
        isSubmitting={isSubmitting}
        category={category}
        onCategoryChange={handleCategoryChange}
        onInputChange={handleInputChange}
        onDateChange={setDate}
        onSubmit={submitForm}
      />
    </TelegramWebAppProvider>
  );
};

export default BenchmarkWebApp;
